import { FiringSignature } from './Signature.js';
import { Flinch } from './Flinch.js';
import { Overheat } from './Overheat.js';
import { SmokeField } from './Smoke.js';
import { Launcher } from './Launcher.js';
import { Thermal } from './Thermal.js';
import { Laser } from './Laser.js';
import { SlideFire } from './SlideFire.js';
import { installHarness } from './Harness.js';

/**
 * Attachment *behaviours* — the gameplay systems that a weapon attachment switches
 * on, as opposed to the numbers an attachment merely edits.
 *
 * The split is deliberate. `src/weapons/` owns the catalogue and the stat-modifier
 * pipeline: it decides *which* behaviours a built loadout enables and with what
 * numbers. This module owns *what those behaviours do*, and knows nothing about
 * attachments, rails or the Gunsmith. The entire interface between the two halves
 * is the plain object at `ctx.behaviours.config` described below, plus a handful of
 * events on the bus.
 *
 * Every field in `config` is read by running code every frame it matters, and every
 * one of them is observable from `window.__behtest`. Nothing here is a stat name
 * waiting for a consumer: if a behaviour could not be wired end to end inside the
 * files this module is allowed to touch, it is absent rather than stubbed, and the
 * reason is written down at the point where it would have gone.
 *
 * ## Contract for the attachment pipeline
 *
 * ```js
 * ctx.behaviours.reset();                       // back to bare-weapon defaults
 * ctx.behaviours.apply({ firingSignature: 0 }); // deep-merge a patch
 * ctx.behaviours.config                         // live, mutable, safe to poke
 * ```
 *
 * `reset()` then `apply()` on every loadout change is the intended cycle; `apply`
 * ignores keys it does not know, so an attachment catalogue can ship a field before
 * the behaviour that reads it exists without silently pretending to work.
 *
 * Two fields deliberately run *past* their obvious bounds, because a conversion
 * that cannot be paid for is an upgrade: `firingSignature` goes to 2 (louder than
 * stock) and `flinchResist` goes to −1 (more flinch than stock). Both are read at
 * their full range by real consumers; see the comments on each.
 *
 * One behaviour is not in this object at all. Slide-to-fire reads
 * `def.mobility.slideFire` straight off the resolved weapon definition, because the
 * attachment resolver already puts it there and mirroring it would be a second
 * source of truth for the same fact.
 *
 * ## Events published
 *
 * | Event | Payload | Meaning |
 * |---|---|---|
 * | `combat:shot` | `{ actor, position, signature, isPlayer }` | a gun went off, and how much it gave the shooter away |
 * | `player:flinch` | `{ pitch, yaw, damage, resist }` | aim punch actually applied to the player |
 * | `weapon:heat` | `{ heat, ratio, max, locked, venting }` | overheat readout for the HUD |
 * | `launcher:state` | `{ mode, ammo, max, state, ready }` | underbarrel readout for the HUD |
 *
 * ## Update phases
 *
 * Two registrations, no more: `'sim'` for everything that decides what happens, and
 * `'ui'` for everything that has to sit on the *final* camera for the frame. The
 * camera rig publishes its transform during `'fx'`, so a laser beam or a thermal
 * pass placed during `'sim'` or `'fx'` would be welded to last frame's muzzle.
 */

/** Bare-weapon defaults. `reset()` restores exactly this, deeply. */
export function defaultConfig() {
  return {
    /**
     * How much a shot gives its shooter away. **0 .. 2**, stock is 1.
     *
     * Both consumers scale linearly with it: the minimap holds a hostile's blip
     * for `fireReveal * signature` seconds, and hostiles hear the player's shot at
     * `hearingRange * signature` metres with localisation error to match. Values
     * above 1 are meaningful and are how a muzzle brake pays for its recoil
     * control; 0 is silent on both channels. Clamped at 2 because the hearing
     * radius is linear in it and a larger value would alert the whole level.
     */
    firingSignature: 1,

    /**
     * Fraction of incoming aim punch cancelled. **−1 .. 0.85**, stock is 0.
     *
     * Negative means *more* flinch than stock, which is how a quick-ADS conversion
     * pays for its speed; −0.25 is a quarter more aim punch. The top stops short
     * of 1 because immunity is not a thing an attachment may sell, and the bottom
     * stops at −1 (double punch) because below that a single graze ends any
     * exchange regardless of what was bought with it.
     */
    flinchResist: 0,

    /**
     * Belt-fed conversion. `enabled` off means the weapon has no heat model at all
     * and the system costs one boolean test per frame.
     */
    overheat: {
      enabled: false,
      /** Heat added per round, in units of the 0..1 bar. 0.055 -> ~18 rounds. */
      heatPerShot: 0.055,
      /** Bar per second while cooling normally. */
      coolRate: 0.30,
      /** Bar per second while locked out. Deliberately slower: the lockout is the
       *  punishment, and a vent that clears as fast as it fills is not one.
       *  Only consulted when `lockTime` is 0. */
      ventRate: 0.20,
      /**
       * Seconds a lockout lasts, which is what an attachment actually authors — no
       * one designing a belt box thinks in bar-per-second. Non-zero wins over
       * `ventRate`; the conversion needs `resumeAt`, so it lives in `Overheat`
       * rather than in whoever wrote the patch.
       */
      lockTime: 0,
      /** Heat must fall back to this fraction before the weapon fires again. */
      resumeAt: 0.45,
      /** Seconds of no fire before cooling starts, so tapping is not free. */
      coolDelay: 0.35,
    },

    /**
     * Underbarrel launcher. `mode` selects the payload; 'none' disables the whole
     * alt-fire including its input hook.
     */
    launcher: {
      /** 'none' | 'grenade' | 'smoke' */
      mode: 'none',
      ammo: 0,
      max: 2,
      /** Seconds to bring the launcher up before it can fire. */
      deploy: 0.55,
      /** Seconds to get back on the primary afterwards. */
      stow: 0.45,
      /** Launch speed, m/s. Low enough that the arc is the aiming problem. */
      muzzleVelocity: 34,
      /** Explosive payload. */
      damage: 130,
      radius: 6.5,
    },

    /** Thermal / target-highlight optic. */
    thermal: {
      enabled: false,
      /** Half-angle of the optic's view, degrees. Outside this the highlight is
       *  off, so the optic never reports contacts it is not pointed at. */
      halfAngle: 24,
      /** Metres. Beyond this a body is not resolvable as a heat source. */
      range: 140,
    },

    /** Aiming laser. */
    laser: {
      enabled: false,
      /** Beam visibility while aimed. A laser you can see through your own optic
       *  is a distraction; the dot stays either way. */
      beamWhileAds: false,
      /** Colour of the beam and dot, 0xRRGGBB. */
      colour: 0xff2b1e,
    },
  };
}

export class BehaviourSystem {
  constructor(ctx) {
    this.ctx = ctx;
    this.config = defaultConfig();

    this.signature = new FiringSignature(ctx, this);
    this.flinch = new Flinch(ctx, this);
    this.overheat = new Overheat(ctx, this);
    this.smoke = new SmokeField(ctx, this);
    this.launcher = new Launcher(ctx, this, this.smoke);
    this.thermal = new Thermal(ctx, this);
    this.laser = new Laser(ctx, this);
    // Reads `def.mobility.slideFire` off the resolved weapon rather than this
    // config object, so it needs no entry in the contract below.
    this.slideFire = new SlideFire(ctx, this);

    /* ---- profiling. Two accumulators so an idle frame is honestly reported. ---- */
    this.ms = 0;
    this.msPeak = 0;
    this._msAcc = 0;
    this._msN = 0;

    this._unreg = [
      ctx.engine.registerUpdate((dt, t) => this._sim(dt, t), 'sim'),
      ctx.engine.registerUpdate((dt, t) => this._present(dt, t), 'ui'),
    ];

    ctx.behaviours = this;
    installHarness(ctx, this);
  }

  /* ------------------------------ config API ------------------------------- */

  /** Restore bare-weapon defaults. Call before applying a new attachment set. */
  reset() {
    deepAssign(this.config, defaultConfig(), true);
    this.overheat.onConfigChanged();
    this.launcher.onConfigChanged();
    this.thermal.onConfigChanged();
    this.laser.onConfigChanged();
    return this.config;
  }

  /**
   * Deep-merge a patch into the live config. Unknown keys are dropped and reported
   * once: a behaviour that does not exist must not look like it was configured.
   */
  apply(patch) {
    if (patch) deepAssign(this.config, patch, false);
    this.overheat.onConfigChanged();
    this.launcher.onConfigChanged();
    this.thermal.onConfigChanged();
    this.laser.onConfigChanged();
    return this.config;
  }

  /* --------------------------------- frame --------------------------------- */

  _sim(dt, elapsed) {
    const t0 = performance.now();
    this.overheat.update(dt, elapsed);
    this.launcher.update(dt, elapsed);
    this.smoke.update(dt, elapsed);
    this.laser.simulate(dt, elapsed);
    this._msAcc += performance.now() - t0;
  }

  _present(dt, elapsed) {
    if (this.ctx.engine.paused) return;
    const t0 = performance.now();
    this.smoke.present(dt, elapsed);
    this.thermal.present(dt, elapsed);
    this.laser.present(dt, elapsed);
    const ms = this._msAcc + (performance.now() - t0);
    this._msAcc = 0;
    this._msN++;
    this._msSum = (this._msSum ?? 0) + ms;
    if (this._msN >= 30) {
      this.ms = this._msSum / this._msN;
      this.msPeak = Math.max(this.msPeak * 0.9, ms);
      this._msSum = 0;
      this._msN = 0;
    }
  }

  /** One-line summary of everything switched on, for the harness and the report. */
  report() {
    return {
      ms: +this.ms.toFixed(4),
      peakMs: +this.msPeak.toFixed(4),
      firingSignature: this.config.firingSignature,
      flinchResist: this.config.flinchResist,
      flinch: this.flinch.report(),
      overheat: this.overheat.report(),
      launcher: this.launcher.report(),
      smoke: this.smoke.report(),
      thermal: this.thermal.report(),
      laser: this.laser.report(),
      slideFire: this.slideFire.report(),
      signature: this.signature.report(),
    };
  }

  dispose() {
    for (const off of this._unreg) off();
    this.signature.dispose();
    this.flinch.dispose();
    this.overheat.dispose();
    this.launcher.dispose();
    this.smoke.dispose();
    this.thermal.dispose();
    this.laser.dispose();
    this.slideFire.dispose();
    if (this.ctx.behaviours === this) this.ctx.behaviours = null;
  }
}

/**
 * Bootstrap. Idempotent, so it is safe to call from wherever happens to be
 * constructed first.
 *
 * This is called from `Player`'s constructor rather than from `main.js` because
 * `main.js` belongs to the lead and this module has to be able to stand itself up.
 * Nothing here reads `ctx.player`, `ctx.weapons`, `ctx.ai` or `ctx.fx` at
 * construction time — every one of those is resolved lazily per frame and guarded,
 * which is the same contract the FX and minimap modules already work under.
 */
export function bootstrapBehaviours(ctx) {
  if (ctx.behaviours) return ctx.behaviours;
  try {
    return new BehaviourSystem(ctx);
  } catch (err) {
    console.error('[behaviours] failed to start', err);
    return null;
  }
}

/* -------------------------------------------------------------------------- */

function deepAssign(target, patch, replacing) {
  for (const k in patch) {
    const v = patch[k];
    if (!replacing && !(k in target)) {
      warnUnknown(k);
      continue;
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      if (!target[k] || typeof target[k] !== 'object') target[k] = {};
      deepAssign(target[k], v, replacing);
    } else {
      target[k] = v;
    }
  }
  return target;
}

const _warned = new Set();
function warnUnknown(key) {
  if (_warned.has(key)) return;
  _warned.add(key);
  console.warn(
    `[behaviours] ignoring unknown config key "${key}". Nothing reads it, so it ` +
      `would be a stat that does nothing — add the behaviour first.`
  );
}
